import type { MutableRefObject, ReactNode, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, Column, Columns } from "@aviary";
import type { Sizes } from "@aviary/components/Columns/grid";
import { CatalogEmptyState } from "@shared/components/CatalogEmptyState/CatalogEmptyState";
import { DesktopFilterColumn } from "@shared/components/CatalogSearch/DesktopFilterColumn";
import { SkeletonProductCard } from "@shared/components/SkeletonProductCards/SkeletonProductCard";
import { StickyProductResultsHeader } from "@shared/components/StickyProductResultsHeader";
import type { AnalyticsEvent, SearchApplyEntryPoint } from "@shared/hooks";
import { useCatalogSearch, useBreakpoints } from "@shared/hooks";

import { MobileFilterDrawer } from "../CatalogSearch/MobileFilterDrawer";
import { CatalogSortDropdown } from "../CatalogSortDropdown";
import { ClickToScrollUp } from "../ClickToScrollUp";
import { ProductResultsHeader } from "../ProductResultsHeader";

interface Props {
  children: ReactNode;
  displayedCount: number;
  isLoadingMoreResults?: boolean;
  isLoadingNewResults?: boolean;
  resultCount: number;
  scrollingRef?: MutableRefObject<any>;
  title: string | (ReactElement | string)[];
  overrideEmptyStateMessage?: string;
  overrideEmptyStateHeader?: string;
  onClearAll?: () => void;
  onClearFilters: () => void;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForLogging?: AnalyticsEvent;
  isDrawer?: boolean;
  isCyberMondayEnabled?: boolean;
  onLoadMore?: () => void;
}

const ProductResults = ({
  children,
  displayedCount,
  isLoadingMoreResults,
  isLoadingNewResults,
  resultCount,
  scrollingRef,
  entryPointForLogging,
  eventNameForLogging,
  isDrawer,
  isCyberMondayEnabled,
  onLoadMore,
  title,
  overrideEmptyStateMessage,
  overrideEmptyStateHeader,

  onClearAll,
  onClearFilters,
}: Props) => {
  const { t } = useTranslation("common");
  const { activeSearchType } = useCatalogSearch();
  const { tablet } = useBreakpoints();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;
  const isFiltersActive: boolean = activeSearchType === "filters";

  const gridCardColumnWidth = (): Sizes => {
    if (!tablet.active) {
      return isFiltersActive ? (6 as Sizes) : (4 as Sizes);
    }

    return tablet.active ? (6 as Sizes) : (4 as Sizes);
  };

  const renderSkeletonCatalogProductCards = Array.from({
    length: 6,
  }).map((_, i) => <SkeletonProductCard gridColumnWidth={gridCardColumnWidth()} key={i} />);

  const renderResults = () => {
    if (!isLoadingNewResults && displayedCount === 0) {
      return (
        <CatalogEmptyState
          onClearAll={onClearAll}
          overrideHeader={overrideEmptyStateHeader}
          overrideMessage={overrideEmptyStateMessage}
        />
      );
    }

    if (isLoadingNewResults) {
      return renderSkeletonCatalogProductCards;
    }

    return children;
  };

  const renderLoadMore = !isLoadingNewResults && onLoadMore && (
    <Column key="load-more-button" columnWidth="full">
      <Button onClick={onLoadMore} isLoading={isLoadingMoreResults} isFullWidth>
        {t("LoadMore")}
      </Button>
    </Column>
  );

  const renderHeader = () => {
    return (
      <>
        <ProductResultsHeader
          title={title}
          resultCount={resultCount}
          isLoadingNewResults={isLoadingNewResults}
          isDrawer={isDrawer}
        />
        <StickyProductResultsHeader
          title={title}
          scrollingRef={scrollingRef}
          resultCount={resultCount}
          displayedCount={displayedCount}
          isLoadingNewResults={isLoadingNewResults}
          isDrawer={isDrawer}
        >
          <CatalogSortDropdown isDisabled={isSortViewSelectorDisabled} />
        </StickyProductResultsHeader>
      </>
    );
  };

  return (
    <>
      {renderHeader()}
      <DesktopFilterColumn
        isCyberMondayEnabled={isCyberMondayEnabled}
        entryPointForLogging={entryPointForLogging}
        eventNameForLogging={eventNameForLogging}
        isDrawer={isDrawer}
      >
        <Columns isMultiline>
          {renderResults()}
          {renderLoadMore}
        </Columns>
      </DesktopFilterColumn>
      <ClickToScrollUp scrollingRef={scrollingRef} />
      <MobileFilterDrawer
        resultCount={resultCount}
        onClearFilters={onClearFilters}
        entryPointForLogging={entryPointForLogging}
        isCyberMondayEnabled={isCyberMondayEnabled}
      />
    </>
  );
};

ProductResults.defaultProps = {
  isCyberMondayEnabled: false,
  isDrawer: false,
};

export { ProductResults };
